import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Indent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const { role } = useSelector((state) => state.auth);
  const enteredBy = useSelector((state) => state.auth.id);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();
  const apiEndpoint = `${API_BASE_URL}/indent/indent`;
  const endpoint = `${API_BASE_URL}/adminpage/indent`;

  const getRowColor = useCallback((row) => {
    return row.Status === "Closed" || row.Status === "Force Closed" ? "bg-green-100" : "";
  }, []);

  const getEditFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData.projects?.map((project) => ({
        value: project.id,
        label: project.name,
      })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData.types?.map((type) => ({
        value: type.id,
        label: type.name,
      })) || [],
    },
  ], [dropdownData.projects, dropdownData.types]);

  const formatCreationDate = useCallback((dateStr) => {
    if (!dateStr) return "N/A";
    if (/^\d{2}[\/-]\d{2}[\/-]\d{2,4}$/.test(dateStr)) return dateStr;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }, []);

  const fetchIndents = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await axios.get(endpoint);
      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          IR: indent.IR ? "Approved" : "Pending",
          CreationDate: formatCreationDate(indent.CreationDate),
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
      setDataLoaded(true);
    } catch (error) {
      console.error("Error fetching indents:", error);
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, formatCreationDate, isLoading]);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);
      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };
      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  }, []);

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    const searchTerm = query.toLowerCase().trim();
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.ProjectName,
        indent.TypeName,
        indent.Status,
        indent.CreationDate,
        indent.TaskSchedule,
        indent.L1,
        indent.L2,
        indent.PO,
        indent.PH,
        indent.IR
      ];
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    });
    setFilteredIndents(filtered);
  }, [indents]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(filtered.sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)));
    } else {
      setFilteredIndents(indents);
    }
  }, [indents]);

  const handleRowClick = useCallback((row) => {
    navigate(`/viewTable`, { state: { row } });
  }, [navigate]);

  const handleRowSelect = useCallback((row) => {
    setSelectedRow(row);
  }, []);

  const displayData = useMemo(() => {
    return filteredIndents;
  }, [filteredIndents]);

  const tableColumns = useMemo(() => [
    { key: "IndentID", label: "Indent ID" },
    { key: "CreationDate", label: "Creation Date" },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Indent Type" },
    { key: "TaskSchedule", label: "Task Scheduled" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Item Count" },
  ], []);

  if (!dataLoaded) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p>Loading indents...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">View Table</h3>
        </div>
        {dropdownData.users?.length > 0 && dropdownData.projects?.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields}
            apiEndpoint={apiEndpoint}
            data={displayData}
            selectedRow={selectedRow}
            onRowSelect={handleRowSelect}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole="hhh"
            addLink="/addIndent"
            editRoute="/viewTable"
            editButtonLabel="View"
            searchQuery={searchQuery}
          />
        )}
        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={tableColumns}
            data={displayData}
            selectedRow={selectedRow}
            onRowSelect={handleRowSelect}
            onDoubleClick={handleRowClick}
            rowKey="IndentID"
            userIdKey="UserID"
            getRowClassName={getRowColor}
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole="admin"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Indent;